import React from 'react';

interface LocationControlProps {
  isTracking: boolean;
  hasLocation: boolean;
  error: string | null;
  loading: boolean;
  supported: boolean;
  onRequestLocation: () => void;
  onStopTracking: () => void;
  onCenterOnUser: () => void;
}

const LocationControl: React.FC<LocationControlProps> = ({
  isTracking,
  hasLocation,
  error,
  loading,
  supported,
  onRequestLocation,
  onStopTracking,
  onCenterOnUser
}) => {
  if (!supported) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
        <div className="flex">
          <svg className="w-5 h-5 text-yellow-400 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <p className="text-sm text-yellow-800">
            Geolocation is not supported by your browser.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-4">
      <div className="flex items-center justify-between bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
        <div className="flex items-center space-x-3">
          <div className={`w-3 h-3 rounded-full ${
            loading ? 'bg-yellow-500 animate-pulse' :
            hasLocation ? 'bg-green-50 dark:bg-green-900/200 animate-pulse' :
            error ? 'bg-red-500' : 'bg-gray-300'
          }`} />
          
          <div>
            <p className="text-sm font-medium text-gray-900 dark:text-white flex items-center">
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-primary-500" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Getting your location...
                </>
              ) : hasLocation ? (
                <>
                  <svg className="w-4 h-4 mr-1 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 5.5V6C15 7.1 13.1 8 12 8S9 7.1 9 6V5.5L3 7V9L9 10.5V16H7V22H17V16H15V10.5L21 9Z"/>
                  </svg>
                  Location active
                </>
              ) : error ? (
                'Location unavailable'
              ) : (
                'Location not active'
              )}
            </p>
            {error && (
              <p className="text-xs text-red-600 mt-1">{error}</p>
            )}
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          {hasLocation && (
            <button
              onClick={onCenterOnUser}
              className="p-2 text-primary-600 hover:text-white hover:bg-primary-600 border border-primary-600 rounded-lg transition-all duration-200 hover:shadow-md"
              title="Center map on your location"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            </button>
          )}
          
          {!hasLocation && !isTracking && (
            <button
              onClick={onRequestLocation}
              disabled={loading}
              className="px-3 py-1.5 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Locating...' : 'Enable Location'}
            </button>
          )}
          
          {isTracking && !hasLocation && (
            <button
              onClick={onStopTracking}
              className="px-3 py-1.5 bg-gray-600 text-white text-sm rounded-lg hover:bg-gray-700 transition-colors"
            >
              Stop Tracking
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default LocationControl;
